import { nanoid } from 'nanoid';

import { extractBareText } from '@/shared/utils/extractBareText';
import { type QuizBankProblem } from '@/views/build/features/quiz-bank';
import { type BuildQuizListItem } from '@/views/build/form/schema/BuildQuizListSchema';
import { getBlankQuizItem } from '@/views/build/form/schema/getBlankQuizItem';

import { createCurriculumFingerprint } from './curriculumFingerprint';

/** `question` 은 스키마상 1~300자 평문이다. */
const QUESTION_MAX_LENGTH = 300;

/**
 * 리치텍스트 → 평문. 정규식으로 태그를 지우면 `<math-inline>` 이 통째로 사라져
 * 수식만 있는 문항이 빈 문자열이 되므로 `extractBareText` 를 쓴다.
 */
const toPlainText = (html: string) => extractBareText(html).trim();

const toQuestionText = (html: string) =>
  toPlainText(html).slice(0, QUESTION_MAX_LENGTH);

/**
 * 문제 은행 문항 → 빌드 폼 `quizList` 아이템. 진입점이 둘(신규 생성 모달 · 편집 재진입)이라
 * feature 가 아닌 page-level util 에 둔다.
 *
 * 성취기준은 AI 생성 문항과 같은 `curriculumList` 에 싣고, `curriculumFingerprint` 로
 * 담은 뒤 문항을 고치면 submit 시 성취기준이 떨어져 나가게 한다.
 */
export const toBuildQuizListItems = (
  problems: QuizBankProblem[],
  /** `subject` 는 트리 id 가 아니라 과목명(`수학`)이다 — `QuizCurriculumSchema` 규약 */
  { subject }: { subject: string },
): BuildQuizListItem[] =>
  problems.map(problem => {
    const blank = getBlankQuizItem();

    const base = {
      ...blank,
      id: nanoid(8),
      bankItemId: problem.id,
      type: problem.type,
      question: toQuestionText(problem.stem),
      questionRichText: problem.stem,
      questionImageUrl: problem.imageUrl ?? '',
      hint: problem.hint,
      curriculumList: [{ standardCode: problem.standard.code, subject }],
    };

    const quiz: BuildQuizListItem = (() => {
      switch (problem.type) {
        case 'multiple':
          return {
            ...base,
            multipleData: {
              choiceList: (problem.options ?? []).map((option, index) => ({
                key: nanoid(8),
                text: toPlainText(option.richText || option.text),
                richText: option.richText ?? option.text,
                imageUrl: option.imageUrl,
                isAnswer: problem.answerIndexes?.includes(index) ?? false,
              })),
            },
          };
        case 'subjective':
          return {
            ...base,
            subjectiveData: {
              answerList: (problem.answerTexts ?? []).map(text => ({
                key: nanoid(8),
                text,
              })),
            },
          };
        case 'ox':
          return { ...base, oxData: { answer: problem.oxAnswer ?? 'o' } };
      }
    })();

    // 지문은 유형별 정답 영역까지 포함한 완성 문항으로 계산한다.
    return {
      ...quiz,
      curriculumFingerprint: createCurriculumFingerprint(quiz),
    };
  });
